import React, { useState, useEffect } from 'react';
import { TextField, MenuItem, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from '@material-ui/core';
import { Add } from '@material-ui/icons';
import { ThemeProvider, createMuiTheme, StylesProvider } from '@material-ui/core/styles';
import './confluenceSection.scss';
import url from "../../config.js"

const domain = url.domain;

// Dark theme matching the dropdown
const dropdownTheme = createMuiTheme({
  palette: {
    type: 'dark',
    primary: { main: '#ffffff' },
    background: {
      paper: 'transparent', // ✅ Make transparent to inherit dropdown background
      default: 'transparent',
    },
    text: {
      primary: '#ffffff',
      secondary: '#aaaaaa',
    },
  },
  overrides: {
    MuiOutlinedInput: {
      root: {
        backgroundColor: 'transparent', // ✅ Transparent background
        color: '#ffffff',
        border: '1px solid #444',
        borderRadius: '4px',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#444444',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: '#666666',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#ffffff',
        },
      },
      input: {
        color: '#ffffff',
        padding: '12px 14px',
      },
    },
    MuiSelect: {
      select: {
        '&:focus': {
          backgroundColor: 'transparent',
        },
      },
      icon: {
        color: '#aaaaaa',
      },
    },
    MuiMenuItem: {
      root: {
        backgroundColor: '#1a1a1a',
        color: '#ffffff',
        '&:hover': {
          backgroundColor: '#2a2a2a',
        },
        '&.Mui-selected': {
          backgroundColor: '#333333',
          '&:hover': {
            backgroundColor: '#3a3a3a',
          },
        },
      },
    },
  },
});

function ConfluenceSection({ selectedAccount, onCreatePage }) {
    const [spaces, setSpaces] = useState([]);
    const [selectedSpace, setSelectedSpace] = useState('');
    const [pageName, setPageName] = useState('');
    const [loading, setLoading] = useState(false);
    const [showNewSpaceDialog, setShowNewSpaceDialog] = useState(false);
    const [newSpaceName, setNewSpaceName] = useState('');
    const [creatingSpace, setCreatingSpace] = useState(false);

    useEffect(() => {
        if (selectedAccount) {
            fetchSpaces();
        }
    }, [selectedAccount]);

    const fetchSpaces = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${domain}/api/v1/confluence/getConfluenceSpaces`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': localStorage.getItem('token')
                },
                body: JSON.stringify({
                    confluenceBaseUrl: selectedAccount.confluenceBaseUrl,
                    confluenceEmail: selectedAccount.confluenceEmail,
                    confluencePat: selectedAccount.confluencePat
                })
            });
            const data = await response.json();
            if (data.success) {
                setSpaces(data.spaces);
            }
        } catch (error) {
            console.error('Error fetching spaces:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNewSpace = async () => {
        if (!newSpaceName) {
            alert('Please enter space name');
            return;
        }

        setCreatingSpace(true);
        try {
            const response = await fetch(`${domain}/api/v1/confluence/createConfluenceSpaces`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': localStorage.getItem('token')
                },
                body: JSON.stringify({
                    accountId: selectedAccount._id,
                    confluenceBaseUrl: selectedAccount.confluenceBaseUrl,
                    confluenceEmail: selectedAccount.confluenceEmail,
                    confluencePat: selectedAccount.confluencePat,
                    spaceName: newSpaceName
                })
            });

            const data = await response.json();
            if (data.success) {
                const newSpace = { key: data.key, name: data.name };
                setSpaces([...spaces, newSpace]);
                setSelectedSpace(data.key);
                setShowNewSpaceDialog(false);
                setNewSpaceName('');
            } else {
                alert(data.message || 'Failed to create space');
            }
        } catch (error) {
            console.error('Error creating space:', error);
            alert('Error creating space');
        } finally {
            setCreatingSpace(false);
        }
    };

    const handleCreatePage = () => {
        if (!selectedSpace || !pageName) {
            alert('Please select a space and enter a page name');
            return;
        }

        onCreatePage({
            accountId: selectedAccount._id,
            spaceKey: selectedSpace,
            pageName: pageName
        });
    };

    if (loading) {
        return (
            <div className="confluence-loading">
                <CircularProgress size={20} style={{ color: '#ffffff' }} />
                <span>Loading spaces...</span>
            </div>
        );
    }

    return (
        <StylesProvider injectFirst>
            <ThemeProvider theme={dropdownTheme}>
                {/* Remove the outer confluence-form wrapper */}
                <>
                    {/* Space Selection - matches dropdown styling */}
                    <div className="confluence-section">
                        <div className="confluence-section-title">SPACE:</div>
                        <div className="space-selector-row">
                            <TextField
                                select
                                fullWidth
                                variant="outlined"
                                value={selectedSpace}
                                onChange={(e) => setSelectedSpace(e.target.value)}
                                className="space-select"
                                placeholder="Select a space"
                            >
                                {spaces.length === 0 ? (
                                    <MenuItem disabled>No spaces available</MenuItem>
                                ) : (
                                    spaces.map((space) => (
                                        <MenuItem key={space.key} value={space.key}>
                                            {space.name} ({space.key})
                                        </MenuItem>
                                    ))
                                )}
                            </TextField>
                            <button
                                className="new-space-btn"
                                onClick={() => setShowNewSpaceDialog(true)}
                            >
                                <Add /> NEW SPACE
                            </button>
                        </div>
                    </div>

                    {/* Page Name - matches dropdown styling */}
                    <div className="confluence-section">
                        <div className="confluence-section-title">PAGE NAME:</div>
                        <TextField
                            fullWidth
                            variant="outlined"
                            value={pageName}
                            onChange={(e) => setPageName(e.target.value)}
                            placeholder="Enter page name (e.g., API Design Documentation)"
                            className="page-name-input"
                        />
                    </div>

                    {/* Create Button - matches dropdown button styling */}
                    <button
                        className="confluence-submit-btn"
                        onClick={handleCreatePage}
                        disabled={!selectedSpace || !pageName}
                    >
                        Create Page
                    </button>

                    {/* Dialog for New Space */}
                    <Dialog 
                        open={showNewSpaceDialog} 
                        onClose={() => !creatingSpace && setShowNewSpaceDialog(false)}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>Create New Confluence Space</DialogTitle>
                        <DialogContent>
                            <TextField
                                fullWidth
                                label="Space Name"
                                variant="outlined"
                                value={newSpaceName}
                                onChange={(e) => setNewSpaceName(e.target.value)}
                                placeholder="e.g., My Project Documentation"
                                margin="normal"
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button 
                                onClick={() => setShowNewSpaceDialog(false)}
                                disabled={creatingSpace}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleCreateNewSpace}
                                disabled={creatingSpace || !newSpaceName}
                                variant="contained"
                            >
                                {creatingSpace ? <CircularProgress size={20} /> : 'Create'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            </ThemeProvider>
        </StylesProvider>
    );
}

export default ConfluenceSection;